import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, CheckCircle2, Trash2, AlertTriangle, Clock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALERT_STYLE = {
  atrasada: { label: "Atrasada", cls: "bg-red-50 text-red-700 border-red-200" },
  proxima: { label: "Próxima (30d)", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  futura: { label: "Futura", cls: "bg-slate-50 text-slate-600 border-slate-200" },
};

export default function Maintenance() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", equipment_id: "", frequency_months: 3, last_date: "", notes: "" });

  const load = () => api.get("/maintenance").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.customer_id) {
      api.get(`/customers/${form.customer_id}`).then((r) => setEquipment(r.data.equipment || [])).catch(() => {});
    }
  }, [form.customer_id]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/maintenance", { ...form, frequency_months: Number(form.frequency_months) || 3 });
      toast.success("Manutenção programada!");
      setOpen(false);
      setForm({ customer_id: "", equipment_id: "", frequency_months: 3, last_date: "", notes: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const markDone = async (id) => {
    await api.post(`/maintenance/${id}/done`);
    toast.success("Manutenção registrada! Próxima data recalculada.");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este lembrete?")) return;
    await api.delete(`/maintenance/${id}`);
    load();
  };

  const groups = { atrasada: [], proxima: [], futura: [] };
  items.forEach((m) => groups[m.alert || "futura"].push(m));

  return (
    <div className="space-y-5 animate-in-up" data-testid="maintenance-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Manutenção preventiva</h1>
          <p className="text-sm text-slate-500">Lembretes e histórico de revisões</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-12 bg-sky-600 hover:bg-sky-700 text-white" data-testid="new-maintenance-btn">
              <Plus className="h-4 w-4 mr-2" /> Programar
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Programar manutenção</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Cliente *</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, equipment_id: "" })} required>
                  <SelectTrigger className="h-12" data-testid="maint-customer"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Equipamento</Label>
                <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })} disabled={!equipment.length}>
                  <SelectTrigger className="h-12" data-testid="maint-equipment"><SelectValue placeholder={equipment.length ? "Selecione" : "Sem equipamentos"} /></SelectTrigger>
                  <SelectContent>{equipment.map((e) => <SelectItem key={e.id} value={e.id}>{[e.type, e.brand, e.model].filter(Boolean).join(" · ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Periodicidade (meses)</Label>
                  <Input className="h-12 font-mono-num" type="number" min="1" data-testid="maint-frequency"
                         value={form.frequency_months} onChange={(e) => setForm({ ...form, frequency_months: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Última manutenção</Label>
                  <Input className="h-12" type="date" data-testid="maint-last" value={form.last_date} onChange={(e) => setForm({ ...form, last_date: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Observações</Label>
                <Input className="h-12" data-testid="maint-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <Button type="submit" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" data-testid="maint-save-btn">Salvar</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {["atrasada", "proxima", "futura"].map((g) => (
        <div key={g}>
          <h2 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            {g === "atrasada" ? <AlertTriangle className="h-4 w-4 text-red-600" /> : <Clock className="h-4 w-4 text-slate-400" />}
            Manutenções {g === "atrasada" ? "atrasadas" : g === "proxima" ? "próximas" : "futuras"} ({groups[g].length})
          </h2>
          <div className="grid gap-2">
            {groups[g].length === 0 && <p className="text-xs text-slate-400">Nenhuma.</p>}
            {groups[g].map((m) => (
              <Card key={m.id} className="border-slate-200" data-testid={`maint-card-${m.id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">{m.customer_name}</p>
                    <p className="text-xs text-slate-500">{m.equipment_name} · a cada {m.frequency_months} meses</p>
                    <p className="text-xs text-slate-500">Próxima: {fmtDate(m.next_date)}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge variant="outline" className={`text-[10px] ${ALERT_STYLE[m.alert].cls}`}>{ALERT_STYLE[m.alert].label}</Badge>
                    <Button variant="outline" size="icon" className="h-10 w-10 text-green-600" title="Registrar manutenção feita"
                            onClick={() => markDone(m.id)} data-testid={`maint-done-${m.id}`}>
                      <CheckCircle2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-10 w-10 text-red-500 hover:bg-red-50" onClick={() => remove(m.id)} data-testid={`maint-delete-${m.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
